import template from './input.tpl';
import DomUtil from '@/core/utils/domUtil';
import LogUtil from '@/core/utils/logUtil';
import styles from './input.module.scss';

let inputChangeHandler: ((event: Event) => void) | null = null;
let inputBlurHandler: ((event: Event) => void) | null = null;
let inputEnterHandler: ((event: KeyboardEvent) => void) | null = null;

export default  class YearInput {
    private _target: HTMLElement | null = null;
    private ariaLabel: string;
    private minYear: number;
    private maxYear: number;
    private validationErrorStr: string;
    private yearSteps: number;
    public element: HTMLElement | null = null;
    private inputField: HTMLInputElement | null = null;
    private _onChangeCallBack: ((value: number) => void) | null = null;

    constructor(minYear = 0, maxYear = 0, ariaLabel = 'Year input') {

        this._target = null;

        this.ariaLabel = ariaLabel;
        this.minYear = minYear;
        this.maxYear = maxYear;
        this.validationErrorStr = 'VALIDATION ERROR - Input must be present as digits (string or number):';
        this.yearSteps = 1;

        let config = {
            min: this.minYear,
            max: this.maxYear,
            steps: this.yearSteps,
            aria_label: this.ariaLabel
        };

        this.element = document.createElement('div');
        this.element.innerHTML = template({config: config, styles: styles});
        this.element = this.element.firstChild as HTMLElement;

        this.inputField = this.element as HTMLInputElement;
        this._onChangeCallBack = null;

        inputChangeHandler = () => this.onInputChange();
        inputEnterHandler = (event: KeyboardEvent) => this.onInputKeyUp(event);
        inputBlurHandler = () => this.onInputBlur();

        this.inputField.addEventListener('input', inputChangeHandler);
        this.inputField.addEventListener('blur', inputBlurHandler);
        this.inputField.addEventListener('keyup', inputEnterHandler);

    }

    onInputChange() {
        // we don't want to show more digits than what maxYear has, so we trim excess characters.
        if (this.inputField && this.inputField.value.length > this.maxYear.toString().length) {
            this.inputField.value = this.trimInputLength(this.inputField.value);
        }

        // prevent updates if the currently typed value out of bounds.
        if (this.inputField && Number(this.inputField.value) >= this.minYear && Number(this.inputField.value) <= this.maxYear) {
            this.inputValue = Number(this.inputField.value);
        }
    }

    onInputBlur() {
        if (this.inputField) {
            this.inputValue = this.resolveValue(Number(this.inputField.value));
        }
    }

    onInputKeyUp(event: KeyboardEvent) {

        // listen to enter-key and if the current value is above maxYear
        if (this.inputField && (event.keyCode === 13 ||
            Number(this.inputField.value) > this.maxYear)) {

            this.inputValue = this.resolveValue(Number(this.inputField.value));
        }
    }

    validateValue(value: any): boolean {

        // This is currently only checking if a value is present, and that it is a valid number.
        // This is because we want to resolve values depending on a interval (between min and max)
        return !isNaN(parseInt(value)) && value >= 0;
    }

    trimInputLength(value: string): string {
        // trim characters on the right side
        return value.slice(0, this.maxYear.toString().length);
    }

    resolveValue(value: any): number {

        // if a value outside of given limits are given, set the closest valid value (min or max).
        if (value < this.minYear) {
            value = this.minYear;
        }
        else if (value > this.maxYear) {
            value = this.maxYear;
        }

        return value;
    }

    set inputValue(value: number) {

        if (this.validateValue(value)) {

            if (this._onChangeCallBack) {
                this._onChangeCallBack(value);
            }

            if (this.inputField) {
                this.inputField.value = String(value);
            }
        }
        else {
            LogUtil.error(this.validationErrorStr, typeof value, value);
        }
    }

    get inputValue(): number {
        return Number(this.inputField?.value) || 0;
    }


    setOnChangeCallback(callback: (value: number) => void) {
        this._onChangeCallBack = callback;
    }


    destroy() {
        if (this.inputField) {

            if(inputChangeHandler) {
                this.inputField.removeEventListener('input', inputChangeHandler);
                inputChangeHandler = null;
            }

            if(inputBlurHandler) {
                this.inputField.removeEventListener('blur', inputBlurHandler);
                inputBlurHandler = null;
            }

            if(inputEnterHandler) {
                this.inputField.removeEventListener('keyup', inputEnterHandler);
                inputEnterHandler = null;
            }
        }

        if (this._target && this.element && DomUtil.isDescendant(this._target, this.element)) {
            this._target.removeChild(this.element);
        }

        this.element = null;
    }

    appendTo(target: HTMLElement) {
        this._target = target;
        if (this.element) {
            this._target.appendChild(this.element);
        }
    }

}
